import { Loader2, Plus, Rocket as RocketIcon, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { CreateMission, MissionRecord } from '@trajectory/contracts';
import { MissionCreationWizard } from '@/components/MissionCreationWizard';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import {
  useCatalogBodies,
  useCatalogLaunchSites,
  useCatalogOrbitPresets,
} from '@/hooks/useCatalog';
import { useCreateMission, useDeleteMission, useMissionList } from '@/hooks/useMissions';
import { useRocketList } from '@/hooks/useRockets';
import {
  clearMission,
  loadMissionRecord,
  useMissionStore,
} from '@/stores/missionStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useUiStore } from '@/stores/uiStore';

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function selectMission(mission: MissionRecord): void {
  loadMissionRecord(mission);
  useSelectionStore.getState().select({ kind: 'mission' });
}

export function MissionExplorerPanel() {
  const [query, setQuery] = useState('');
  const showCreateWizard = useUiStore((state) => state.missionWizardOpen);
  const setShowCreateWizard = useUiStore((state) => state.setMissionWizardOpen);
  const debouncedQuery = useDebouncedValue(query, 300);
  const { data, isLoading, isError, refetch } = useMissionList(debouncedQuery);
  const launchSites = useCatalogLaunchSites();
  const bodies = useCatalogBodies();
  const orbitPresets = useCatalogOrbitPresets();
  const rockets = useRocketList();
  const createMission = useCreateMission();
  const deleteMission = useDeleteMission();
  const selectedMissionId = useMissionStore((state) => state.id);

  // Both handlers report their own failures: they are fired from `void
  // handle…()` click handlers, so an unhandled rejection would otherwise leave
  // the user staring at a button that visibly did nothing.
  const handleCreate = async (payload: CreateMission) => {
    try {
      const created = await createMission.mutateAsync(payload);
      selectMission(created);
      setShowCreateWizard(false);
      useUiStore.getState().notify({
        kind: 'success',
        title: `Created "${created.name}"`,
        message: 'Continue in Inspector, Rocket Editor, or Orbit Editor whenever you are ready.',
      });
    } catch (error) {
      useUiStore.getState().notify({
        kind: 'danger',
        title: 'Could not create the mission',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleDelete = async (mission: MissionRecord) => {
    if (!window.confirm(`Delete mission "${mission.name}"? This cannot be undone.`)) return;
    try {
      await deleteMission.mutateAsync({ id: mission.id, version: mission.version });
    } catch (error) {
      useUiStore.getState().notify({
        kind: 'danger',
        title: `Could not delete "${mission.name}"`,
        message: error instanceof Error ? error.message : String(error),
      });
      return;
    }
    if (selectedMissionId === mission.id) clearMission();
    useUiStore.getState().notify({ kind: 'info', title: `Deleted "${mission.name}"` });
  };

  return (
    <div className="flex h-full flex-col text-sm text-text">
      <div className="flex items-center gap-2 border-b border-border p-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search missions…"
          className="min-w-0 flex-1 rounded-control border border-border bg-surface px-2 py-1 text-xs text-text outline-none focus:border-accent"
        />
        <button
          type="button"
          onClick={() => setShowCreateWizard(true)}
          disabled={createMission.isPending}
          title="New mission"
          aria-label="Create a new mission"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-control text-muted hover:bg-surface-sunken hover:text-text disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className="p-2 pb-0">
        <PanelLearningBanner panelId="mission-explorer" />
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center gap-2 p-4 text-xs text-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading missions…
          </div>
        )}
        {isError && (
          <div role="alert" className="m-3 rounded-panel border border-danger/30 p-3 text-xs">
            <p className="font-medium text-danger">Could not load missions</p>
            <p className="mt-1 leading-relaxed text-muted">
              The API may be unavailable. Existing server data has not been changed.
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 rounded-control border border-border px-2 py-1 text-text hover:bg-surface-sunken"
            >
              Retry
            </button>
          </div>
        )}
        {data && data.items.length === 0 && (
          <div className="m-3 rounded-panel border border-dashed border-border p-4 text-center text-xs">
            <p className="font-medium text-text">No missions yet</p>
            <p className="mt-1 leading-relaxed text-faint">
              The setup wizard explains every required decision and lets you skip optional detail.
            </p>
            <button
              type="button"
              onClick={() => setShowCreateWizard(true)}
              className="mt-3 rounded-control bg-accent px-3 py-1.5 font-medium text-accent-contrast"
            >
              Create your first mission
            </button>
          </div>
        )}
        {/* Select and delete are siblings rather than a button nested inside a
            button: the latter is invalid HTML, and the delete affordance was
            `tabIndex={-1}`, so it could not be reached from the keyboard at
            all. */}
        {data?.items.map((mission) => (
          <div
            key={mission.id}
            className={`group flex w-full items-center gap-2 border-b border-border pr-2 hover:bg-surface-sunken ${
              selectedMissionId === mission.id ? 'bg-surface-sunken' : ''
            }`}
          >
            <button
              type="button"
              onClick={() => selectMission(mission)}
              aria-current={selectedMissionId === mission.id}
              className="min-w-0 flex-1 px-3 py-2 text-left"
            >
              <span className="block truncate font-medium text-text">{mission.name}</span>
              <span className="flex items-center gap-1 text-xs text-faint">
                <RocketIcon className="h-3 w-3" />
                {mission.missionType.toUpperCase()}
                {mission.rocketId ? '' : ' · no rocket'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => void handleDelete(mission)}
              aria-label={`Delete mission ${mission.name}`}
              title="Delete mission"
              className="shrink-0 rounded-control p-1 text-faint opacity-0 hover:bg-surface hover:text-danger focus-visible:opacity-100 group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <MissionCreationWizard
        open={showCreateWizard}
        onOpenChange={setShowCreateWizard}
        launchSites={launchSites.data ?? []}
        bodies={bodies.data ?? []}
        orbitPresets={orbitPresets.data ?? []}
        rockets={rockets.data ?? []}
        isLoadingCatalog={
          launchSites.isLoading || bodies.isLoading || orbitPresets.isLoading || rockets.isLoading
        }
        catalogError={
          launchSites.isError || bodies.isError || orbitPresets.isError || rockets.isError
            ? 'One or more catalogue services could not be loaded. Check the API connection, then retry.'
            : undefined
        }
        onRetryCatalog={() => {
          void Promise.all([
            launchSites.refetch(),
            bodies.refetch(),
            orbitPresets.refetch(),
            rockets.refetch(),
          ]);
        }}
        isSubmitting={createMission.isPending}
        onSubmit={handleCreate}
      />
    </div>
  );
}
